/**
 * VersionIndicator.jsx
 *
 * A simple, isolated version display component.
 * Shows a small translucent version number in the bottom-left corner.
 *
 * Hidden feature:
 * - Tapping the version text 7 times within 4 seconds dispatches a
 *   `cheats-portal-open` custom event that GameContainer listens for.
 *
 * ISOLATION NOTES:
 * - This component is READ-ONLY for version data
 * - Does NOT interact with service workers
 * - Does NOT manage caches
 * - Does NOT affect PWA installation flow
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const REQUIRED_TAPS = 7;
const TAP_WINDOW_MS = 4000;

export function VersionIndicator() {
  const [version, setVersion] = useState(null);
  const tapsRef = useRef([]);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();
          setVersion(data.version);
        }
      } catch (error) {
        console.debug('Version fetch failed:', error);
      }
    };

    fetchVersion();
  }, []);

  const handleTap = useCallback(() => {
    const now = Date.now();
    tapsRef.current = [...tapsRef.current, now].filter(
      (t) => now - t <= TAP_WINDOW_MS
    );

    if (tapsRef.current.length >= REQUIRED_TAPS) {
      tapsRef.current = [];
      window.dispatchEvent(new CustomEvent('cheats-portal-open'));
    }
  }, []);

  if (!version) return null;

  return (
    <button
      type="button"
      onClick={handleTap}
      data-testid="version-indicator"
      className="fixed bottom-3 left-3 z-40 select-none bg-transparent border-0 p-1 cursor-pointer"
      style={{
        fontSize: '12px',
        fontFamily: 'monospace',
        fontWeight: '500',
        color: 'rgba(100, 100, 100, 0.6)',
        textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)',
        letterSpacing: '0.5px',
        WebkitTapHighlightColor: 'transparent',
      }}
      aria-label={`App version ${version}`}
    >
      v{version}
    </button>
  );
}

export default VersionIndicator;
